import React from 'react';
import { useQuery } from 'react-query';
import { MapPin } from 'lucide-react';
import { api } from '../../services/api';
import { SiteMapView, LocksCard, normalizeMarks, SiteMarksPayload } from '../SiteMapEditor';

/**
 * The yard, while the crew is on it (owner, Nov 4 2026: "make the site map
 * visible once they arrive on the job, instead of the route map, and once they
 * end, the route map shows back up").
 *
 * The day timer shows the route board (every stop, the van, the next house)
 * while they are driving; the moment a stop is started this card takes its
 * place — the property's site map with its beds, gates and locks — and the
 * route board comes back when the stop is finished. The marks come from the
 * same place the job page reads (GET /properties/:id/site-marks: a crew login
 * with a visit here this week or next; a 403 is "nothing to show"). A yard
 * with no marks still shows its pin, so there is always a map of where they
 * are; a yard with no pin either falls back to whatever the caller renders.
 */
export default function SiteMapOnStop({ propertyId, label, fallback }: { propertyId: number; label?: string | null; fallback: React.ReactNode }) {
  const { data, isLoading } = useQuery(['site-marks', propertyId],
    () => api.get(`/properties/${propertyId}/site-marks`).then(r => r.data as SiteMarksPayload), { retry: false });
  const marks = normalizeMarks(data?.marks);
  const p = data?.property;
  const pin: [number, number] | null = p && p.lat != null && p.lng != null ? [p.lat, p.lng] : null;
  if (isLoading) return <>{fallback}</>;
  if (!marks.length && !pin) return <>{fallback}</>;
  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-emerald-700" />
          <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-800">This yard{label ? ` · ${label}` : ''}</p>
        </div>
        <SiteMapView marks={marks} pin={pin} className="h-72" />
      </div>
      {marks.length > 0 && <LocksCard marks={marks} />}
    </div>
  );
}
